/**
 * Clean-up for what xterm copies from a CLASSIC (non-full-screen) Claude Code
 * window with the mouse (Pro Builder).
 *
 * xterm's selection is the window's raw rows: hard line breaks where the window
 * wrapped, a 2-space gutter on every answer row, and the response bullet when
 * the drag starts at column 0. What the person meant to copy is the text
 * Claude Code's own full-screen copy produces: wrapped rows joined with one
 * space, no gutter, no bullet, code indentation and table box characters kept.
 *
 * Only xterm's own selection goes through here. A program's OSC 52 copy is
 * already clean and is never touched (`osc52-clipboard.ts`).
 *
 * The rules are deliberately conservative: the gutter is removed only when the
 * whole selection looks like Claude Code answer rows (least indent exactly 2),
 * and rows are joined only when the window must have wrapped them — the next
 * word would not have fit, the next row is not indented deeper than the line
 * it would continue, and the line is not itself code (4+ leading spaces after
 * the gutter, which Claude Code never wraps prose at, or a row straight after
 * such a code row). A token the window hard-cut (a token longer than a whole
 * row, ending exactly at the last column) and text in a script written without
 * spaces (ideographs, kana — NOT Hangul, which separates words) is rejoined
 * without a space. Shell output whose least indent is not 2 (0, or 4 and more)
 * therefore comes out as xterm copied it apart from the padding at each row's
 * end; shell output that is uniformly indented by exactly 2 is
 * indistinguishable from an answer and IS dedented by 2 (and its wrapped rows
 * joined) like one.
 *
 * A selection that starts after the row's first character is judged by the
 * WHOLE first row (`CleanCopyContext.firstRow`): its indent, response bullet
 * and list marker are read from there, so a drag that starts after a bullet's
 * marker still recognises the answer and rejoins the bullet's continuation row.
 * The output still starts at `startCol`.
 *
 * Two known limits, both of which leave a copy slightly off rather than losing
 * text. (1) Top-level code at the gutter (no indent after it) that ends near the
 * right edge, with no indented code row above it in the selection, reads as
 * wrapped prose and can be joined to the row below it. (2) A long INDENTED code
 * line that the window itself wrapped pastes as two lines, because code is
 * never rejoined.
 */

export interface CleanCopyContext {
  /** Screen column (0-based) where the selection starts — `getSelectionPosition().start.x`. */
  startCol: number;
  /** The terminal's width in columns. */
  cols: number;
  /**
   * The whole screen row the selection starts on (`term.buffer.active.getLine(start.y)?.translateToString(true)`).
   * Optional: without it row 0 is judged from the selected text alone, which
   * misreads a drag that starts after a list marker or mid-sentence.
   */
  firstRow?: string;
}

const GUTTER = 2;
const BULLET = /^[●⏺] /;
const LIST_MARKER = /^(?:[-*•] |\d+[.)] )/;
const BOX_DRAWING = /[─-╿]/;
const ZERO_WIDTH = /^(?:\p{Mn}|\p{Me}|[​-‍︀-️\u{1F3FB}-\u{1F3FF}\u{E0020}-\u{E007F}])$/u;
const EMOJI_PRESENTATION = /^\p{Emoji_Presentation}$/u;

/** Hangul (jamo, compatibility jamo, enclosed, syllables, halfwidth): wide, but written with spaces between words. */
function isHangul(cp: number): boolean {
  return (
    (cp >= 0x1100 && cp <= 0x11ff) ||
    (cp >= 0x3130 && cp <= 0x318f) ||
    (cp >= 0x3200 && cp <= 0x321f) ||
    (cp >= 0x3260 && cp <= 0x327f) ||
    (cp >= 0xa960 && cp <= 0xa97f) ||
    (cp >= 0xac00 && cp <= 0xd7ff) ||
    (cp >= 0xffa0 && cp <= 0xffdc)
  );
}

function isEastAsianWide(cp: number): boolean {
  return (
    (cp >= 0x1100 && cp <= 0x115f) ||
    (cp >= 0x2e80 && cp <= 0x303e) ||
    (cp >= 0x3041 && cp <= 0x33ff) ||
    (cp >= 0x3400 && cp <= 0x4dbf) ||
    (cp >= 0x4e00 && cp <= 0x9fff) ||
    (cp >= 0xa000 && cp <= 0xa4cf) ||
    (cp >= 0xac00 && cp <= 0xd7a3) ||
    (cp >= 0xf900 && cp <= 0xfaff) ||
    (cp >= 0xfe30 && cp <= 0xfe6f) ||
    (cp >= 0xff00 && cp <= 0xff60) ||
    (cp >= 0xffe0 && cp <= 0xffe6) ||
    (cp >= 0x20000 && cp <= 0x3fffd)
  );
}

/** A script written without spaces between words (ideographs, kana, fullwidth forms): a line may break between any two of its characters. Hangul is wide but is not one. */
function isNoSpaceScript(cp: number): boolean {
  return isEastAsianWide(cp) && !isHangul(cp);
}

/** Terminal cells a string occupies: combining marks, variation selectors, ZWJ and skin tones 0; wide and emoji-presentation code points 2; the rest 1. */
export function displayWidth(text: string): number {
  let width = 0;
  for (const ch of text) {
    if (ZERO_WIDTH.test(ch)) continue;
    const cp = ch.codePointAt(0)!;
    width += isEastAsianWide(cp) || EMOJI_PRESENTATION.test(ch) ? 2 : 1;
  }
  return width;
}

/** On-screen indent of a non-empty row: its leading spaces (plus `startCol` for the selection's first row); a leading response bullet counts as 2. */
function indentOf(row: string, startCol: number): number {
  const lead = row.length - row.trimStart().length;
  return startCol + (lead > 0 ? lead : BULLET.test(row) ? GUTTER : 0);
}

/** The on-screen indent of the selection's first row: read from the whole screen row when the context carries it, else from the selected text plus `startCol`. */
function firstRowIndent(row: string, ctx: CleanCopyContext): number {
  const full = ctx.firstRow?.trimEnd();
  return full ? indentOf(full, 0) : indentOf(row, ctx.startCol);
}

/**
 * True when the selection looks like Claude Code answer rows: every later
 * non-empty row is indented at least 2 and the least-indented row of the whole
 * selection (first row included) is indented exactly 2. A selection whose
 * least indent is 0 (shell output), 4 or more (indented shell output, or one
 * that starts inside nested code) is left alone.
 */
function hasGutter(rows: string[], ctx: CleanCopyContext): boolean {
  let min = Infinity;
  for (let i = 0; i < rows.length; i++) {
    if (rows[i] === '') continue;
    const indent = i === 0 ? firstRowIndent(rows[0], ctx) : indentOf(rows[i], 0);
    if (i > 0 && indent < GUTTER) return false;
    min = Math.min(min, indent);
  }
  return min === GUTTER;
}

function stripGutter(rows: string[], startCol: number): string[] {
  return rows.map((row, i) => {
    if (row === '') return row;
    if (BULLET.test(row)) return row.slice(GUTTER);
    if (i === 0) {
      const lead = row.length - row.trimStart().length;
      return row.slice(Math.min(Math.max(GUTTER - startCol, 0), lead));
    }
    return row.slice(GUTTER);
  });
}

/** Leading spaces of a row. */
function leadOf(row: string): number {
  return row.length - row.trimStart().length;
}

/** Leading spaces (after the gutter) from which a non-list row is code, not wrapped prose. */
const CODE_INDENT = 4;

/** Where a logical line's wrapped rows continue, and whether the line is code (never joined). */
interface LineShape {
  indent: number;
  code: boolean;
}

function lineShape(first: string): LineShape {
  const lead = leadOf(first);
  const marker = LIST_MARKER.exec(first.trimStart());
  if (marker) return { indent: lead + marker[0].length, code: false };
  return { indent: lead, code: lead >= CODE_INDENT };
}

/** The screen column row `i` of the selection ends at. */
function screenEnd(raw: string[], i: number, ctx: CleanCopyContext): number {
  return (i === 0 ? ctx.startCol : 0) + displayWidth(raw[i]);
}

/** The first word of a row's text for a "would it have fit" test: a script without spaces may break after any one character, so that character alone. */
function firstWordOf(next: string): string {
  const cp = next.codePointAt(0)!;
  return isNoSpaceScript(cp) ? String.fromCodePoint(cp) : next.split(/\s/, 1)[0];
}

/** True when row `i` and `i + 1` were one line (of shape `shape`) that the window wrapped. */
function wasWrapped(raw: string[], body: string[], i: number, ctx: CleanCopyContext, shape: LineShape): boolean {
  const here = body[i];
  const next = body[i + 1].trimStart();
  if (here === '' || next === '' || shape.code) return false;
  if (LIST_MARKER.test(next) || BOX_DRAWING.test(here) || BOX_DRAWING.test(next)) return false;
  if (leadOf(body[i + 1]) > shape.indent) return false;
  return screenEnd(raw, i, ctx) + 1 + displayWidth(firstWordOf(next)) > ctx.cols;
}

const lastCodePoint = (text: string) => [...text].at(-1)!.codePointAt(0)!;

/** '' when the window cut a token longer than a whole row, or text in a script written without spaces, in two; otherwise the single space a wrap stands for. */
function joinSeparator(raw: string[], body: string[], i: number, line: string, ctx: CleanCopyContext): string {
  const next = body[i + 1].trimStart();
  if (isNoSpaceScript(lastCodePoint(line)) && isNoSpaceScript(next.codePointAt(0)!)) return '';
  // The window cuts a token only when it is longer than a whole row, so the row is full AND both halves together would not have fitted a row.
  const lastToken = body[i].split(/\s+/).at(-1)!;
  const hardCut = screenEnd(raw, i, ctx) === ctx.cols && displayWidth(lastToken) + displayWidth(firstWordOf(next)) > ctx.cols - GUTTER;
  return hardCut ? '' : ' ';
}

/** The whole first screen row with its gutter or bullet removed, for judging the first line's shape; null without `ctx.firstRow`. */
function firstRowBody(ctx: CleanCopyContext): string | null {
  const full = ctx.firstRow?.trimEnd();
  if (!full) return null;
  return BULLET.test(full) ? full.slice(GUTTER) : full.slice(Math.min(GUTTER, leadOf(full)));
}

function joinWrapped(raw: string[], body: string[], ctx: CleanCopyContext): string[] {
  const firstBody = firstRowBody(ctx);
  const out: string[] = [];
  let afterCode: boolean = false;
  for (let i = 0; i < body.length; i++) {
    let line = body[i];
    const own = lineShape(i === 0 && firstBody !== null && body[0] !== '' ? firstBody : body[i]);
    // A row straight after a code row (no blank between, not a list item) is code too: a top-level `y = f()` after an indented block is not prose.
    const shape: LineShape = afterCode && body[i] !== '' && !LIST_MARKER.test(body[i].trimStart()) ? { ...own, code: true } : own;
    afterCode = body[i] !== '' && shape.code;
    while (i + 1 < body.length && wasWrapped(raw, body, i, ctx, shape)) {
      line = `${line}${joinSeparator(raw, body, i, line, ctx)}${body[i + 1].trimStart()}`;
      i++;
    }
    out.push(line);
  }
  return out;
}

/** The classic screen can carry an extra blank cell after a wide emoji; an emoji followed by exactly two spaces becomes the emoji and one. A table row keeps its spacing, so its columns stay aligned. */
function collapseEmojiGap(row: string): string {
  if (BOX_DRAWING.test(row)) return row;
  return row.replace(/([\p{Emoji_Presentation}\p{Emoji_Modifier}\uFE0F]) {2}(?! )/gu, '$1 ');
}

export function cleanCopy(raw: string, ctx: CleanCopyContext): string {
  if (raw === '') return raw;
  const rows = raw.split('\n').map((row) => row.replace(/\s+$/, ''));
  if (!hasGutter(rows, ctx)) return rows.join('\n');
  const body = stripGutter(rows, ctx.startCol);
  const joined = joinWrapped(rows, body, ctx);
  const text = joined.map(collapseEmojiGap).join('\n');
  return text === '' ? raw.replace(/\s+$/, '') : text;
}
